// Dashboard.jsx — Trading dashboard (light theme)
import React, { useCallback, useEffect, useRef, useState } from "react";
import LiveAlerts from "../components/LiveAlerts";
import OptionsChain from "../components/OptionsChain";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const POLL_FAST = 5_000;
const POLL_SLOW = 30_000;
const SIGNALS_PER_PAGE = 10;

function fmt(n, dec = 2) {
  if (n == null) return "—";
  return Number(n).toLocaleString("en-IN", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtCr(n) {
  if (n == null) return "—";
  const v = Number(n);
  return `${v >= 0 ? "+" : ""}${Math.abs(v) >= 1000 ? (v / 1000).toFixed(1) + "K" : v.toFixed(0)} Cr`;
}
function chgColor(v) {
  if (v == null) return "text-gray-400";
  return v >= 0 ? "text-green-600" : "text-red-600";
}
// Format contract counts as Sensibull-style: 2,25,000 → 2.25L / 50,000 → 50.0K
function fmtL(n, showSign = true) {
  if (n == null) return null;
  const abs = Math.abs(n);
  const s = showSign ? (n >= 0 ? "+" : "-") : (n < 0 ? "-" : "");
  if (abs >= 100000) return `${s}${(abs / 100000).toFixed(2)}L`;
  if (abs >= 1000)   return `${s}${(abs / 1000).toFixed(1)}K`;
  return `${showSign && n > 0 ? "+" : ""}${n}`;
}
function viewBg(v) {
  if (!v) return "";
  if (v.includes("Strong Bull")) return "bg-green-100 text-green-700";
  if (v.includes("Bull"))        return "bg-green-50 text-green-600";
  if (v.includes("Strong Bear")) return "bg-red-100 text-red-700";
  if (v.includes("Bear"))        return "bg-red-50 text-red-500";
  return "bg-gray-100 text-gray-400";
}
function viewShort(v) {
  if (!v) return null;
  if (v.includes("Strong Bull")) return { text: "▲▲", cls: "text-green-600 font-bold" };
  if (v.includes("Bull"))        return { text: "▲",  cls: "text-green-500" };
  if (v.includes("Strong Bear")) return { text: "▼▼", cls: "text-red-600 font-bold" };
  if (v.includes("Bear"))        return { text: "▼",  cls: "text-red-500" };
  return { text: "~", cls: "text-gray-400" };
}

// ── Index price card ──────────────────────────────────────────────────────────
function IndexCard({ name, data, pcr, eod }) {
  const ltp    = data?.ltp;
  const chg    = data?.chg;
  const chgPct = data?.chg_pct;
  const maxPain = eod?.max_pain;
  const expiry  = eod?.expiry
    ? new Date(eod.expiry).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
    : null;
  const up = chg == null ? null : chg >= 0;

  return (
    <div className={`rounded-xl p-4 border bg-white ${up == null ? "border-gray-200" : up ? "border-green-200" : "border-red-200"}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">{name}</p>
          <p className={`text-2xl font-bold font-mono mt-1 ${up == null ? "text-gray-700" : up ? "text-green-700" : "text-red-700"}`}>
            {ltp != null ? fmt(ltp, 2) : "—"}
          </p>
          {chg != null && (
            <p className={`text-xs font-mono mt-0.5 ${up ? "text-green-600" : "text-red-600"}`}>
              {up ? "▲" : "▼"} {fmt(Math.abs(chg), 2)} ({fmt(Math.abs(chgPct), 2)}%)
            </p>
          )}
        </div>
        <div className="text-right space-y-1">
          {pcr != null && (
            <div>
              <p className="text-gray-400 text-xs">PCR</p>
              <p className={`text-sm font-bold font-mono ${pcr > 1.2 ? "text-green-600" : pcr < 0.8 ? "text-red-600" : "text-yellow-600"}`}>
                {pcr.toFixed(2)}
              </p>
            </div>
          )}
          {maxPain != null && (
            <div>
              <p className="text-gray-400 text-xs">Max Pain</p>
              <p className="text-sm font-mono text-purple-600">{fmt(maxPain, 0)}</p>
              {expiry && <p className="text-gray-400 text-[10px]">{expiry} exp</p>}
            </div>
          )}
        </div>
      </div>
      {(data?.high != null && data?.low != null) && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>L {fmt(data.low, 0)}</span>
            <span>H {fmt(data.high, 0)}</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            {ltp && data.high !== data.low && (
              <div
                className={`h-1.5 rounded-full ${up ? "bg-green-500" : "bg-red-500"}`}
                style={{ width: `${((ltp - data.low) / (data.high - data.low)) * 100}%` }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── VIX card ──────────────────────────────────────────────────────────────────
function VixCard({ vix }) {
  const band =
    vix == null ? { label: "—", color: "text-gray-400", bg: "bg-white border-gray-200" } :
    vix < 12   ? { label: "Very Low — sell premium", color: "text-blue-600",   bg: "bg-white border-blue-200" } :
    vix < 16   ? { label: "Normal — directional",    color: "text-green-600",  bg: "bg-white border-green-200" } :
    vix < 20   ? { label: "Moderate — hedge",        color: "text-yellow-600", bg: "bg-white border-yellow-200" } :
    vix < 25   ? { label: "Elevated — small size",   color: "text-orange-600", bg: "bg-white border-orange-200" } :
                 { label: "HIGH — sit out",           color: "text-red-600",    bg: "bg-white border-red-300" };
  const pct = vix ? Math.min(100, (vix / 40) * 100) : 0;

  return (
    <div className={`rounded-xl p-4 border ${band.bg}`}>
      <p className="text-gray-500 text-xs uppercase tracking-wider">India VIX</p>
      <p className={`text-3xl font-bold font-mono mt-1 ${band.color}`}>{vix != null ? vix.toFixed(2) : "—"}</p>
      <p className={`text-xs mt-0.5 ${band.color}`}>{band.label}</p>
      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-3">
        <div className={`h-1.5 rounded-full ${band.color.replace("text-", "bg-")}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Institutional flow ────────────────────────────────────────────────────────
function InstCard({ inst, global: g }) {
  const optNet = (inst?.fii_call_net != null && inst?.fii_put_net != null)
    ? inst.fii_call_net + inst.fii_put_net : null;

  // Each row: { label, indent, header, value, contracts, sub, chg, view, bullish, bearish }
  const sections = [
    {
      key: "futures", title: "Index Futures",
      net: inst?.fii_futures_net_contracts, chg: inst?.fii_futures_chg, view: inst?.view_futures,
      sub: inst?.fii_futures_ls != null ? `L/S ${inst.fii_futures_ls.toFixed(2)}` : null,
      rows: [
        { label: "NIFTY",
          sub: inst?.fii_nifty_futures_ls != null ? `L/S ${inst.fii_nifty_futures_ls.toFixed(2)}` : null,
          val: inst?.fii_nifty_futures_buy != null ? `${fmtL(inst.fii_nifty_futures_buy, false)} B / ${fmtL(inst.fii_nifty_futures_sell, false)} S` : null,
          chg: inst?.fii_nifty_futures_chg, view: inst?.view_nifty_futures },
        { label: "BANKNIFTY",
          sub: inst?.fii_banknifty_futures_ls != null ? `L/S ${inst.fii_banknifty_futures_ls.toFixed(2)}` : null,
          val: inst?.fii_banknifty_futures_buy != null ? `${fmtL(inst.fii_banknifty_futures_buy, false)} B / ${fmtL(inst.fii_banknifty_futures_sell, false)} S` : null,
          chg: inst?.fii_banknifty_futures_chg, view: inst?.view_banknifty_futures },
      ],
    },
    {
      key: "options", title: "Index Options",
      net: optNet, chg: null, view: null,
      rows: [
        { label: "Call",  val: inst?.fii_call_net != null ? fmtL(inst.fii_call_net) : null,
          valColor: inst?.fii_call_net != null ? (inst.fii_call_net >= 0 ? "text-green-600" : "text-red-500") : "text-gray-500",
          chg: inst?.fii_call_chg, view: inst?.view_calls },
        { label: "Put",   val: inst?.fii_put_net != null ? fmtL(inst.fii_put_net) : null,
          valColor: inst?.fii_put_net != null ? (inst.fii_put_net <= 0 ? "text-green-600" : "text-red-500") : "text-gray-500",
          chg: inst?.fii_put_chg, view: inst?.view_puts },
        { label: "NIFTY opts",
          val: inst?.fii_nifty_options_net != null ? `${fmtL(inst.fii_nifty_options_net)} (${fmtL(inst.fii_nifty_options_oi, false)} OI)` : null,
          chg: inst?.fii_nifty_options_chg, neutral: true },
        { label: "BNIFTY opts",
          val: inst?.fii_banknifty_options_net != null ? `${fmtL(inst.fii_banknifty_options_net)} (${fmtL(inst.fii_banknifty_options_oi, false)} OI)` : null,
          chg: inst?.fii_banknifty_options_chg, neutral: true },
      ],
    },
  ];

  const cashRows = [
    { label: "FII Cash", val: inst?.fii_cash_net != null ? fmtCr(inst.fii_cash_net) : null, color: chgColor(inst?.fii_cash_net) },
    { label: "DII Cash", val: inst?.dii_net      != null ? fmtCr(inst.dii_net)       : null, color: chgColor(inst?.dii_net) },
    { label: "DII Fut",  val: inst?.dii_futures_long != null ? `${fmtL(inst.dii_futures_long, false)} L / ${fmtL(inst.dii_futures_short, false)} S` : null, color: "text-gray-500" },
    { label: "DII Opts", val: inst?.dii_options_net != null ? `${fmtL(inst.dii_options_net)} net` : null, color: chgColor(inst?.dii_options_net) },
    { label: "Pro Net ▲",    val: inst?.pro_net    != null ? fmtL(inst.pro_net)    : null, color: chgColor(inst?.pro_net) },
    { label: "Client Net ▼", val: inst?.client_net != null ? fmtL(inst.client_net) : null, color: chgColor(-(inst?.client_net || 0)) },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 text-xs">
      <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-3 font-semibold">Institutional Flow</p>

      {/* FII Futures + Options sections */}
      {sections.map(sec => {
        const vs = viewShort(sec.view);
        return (
          <div key={sec.key} className="mb-3">
            {/* Section header */}
            <div className="flex items-center justify-between bg-gray-50 rounded px-2 py-1 mb-1.5">
              <span className="font-semibold text-gray-700 text-[11px] uppercase tracking-wide">{sec.title}</span>
              <div className="flex items-center gap-1.5">
                {sec.net != null && <span className="font-mono font-semibold text-gray-700">{fmtL(sec.net)}</span>}
                {sec.chg != null && <span className="text-gray-400 text-[10px]">({fmtL(sec.chg)})</span>}
                {vs && <span className={`text-[11px] ${vs.cls}`} title={sec.view}>{vs.text}</span>}
                {sec.net == null && !vs && <span className="text-gray-300">—</span>}
              </div>
            </div>
            {/* Sub-rows: 3 cols — label | value | view */}
            <div className="space-y-1 pl-1">
              {sec.rows.map(r => {
                const rv = viewShort(r.view);
                return (
                  <div key={r.label} className="grid gap-x-1 items-center" style={{ gridTemplateColumns: "auto 1fr auto auto" }}>
                    <span className="text-gray-400 text-[10px] w-16 shrink-0">↳ {r.label}</span>
                    {r.sub && <span className="text-gray-400 text-[10px]">({r.sub})</span>}
                    {!r.sub && <span />}
                    <span className={`font-mono text-right text-[10px] ${r.valColor || (r.neutral ? "text-gray-500" : "text-gray-600")}`}>
                      {r.val ?? "—"}
                    </span>
                    <span className={`text-[11px] text-right w-4 ${rv?.cls || "text-gray-300"}`} title={r.view}>
                      {rv ? rv.text : (r.chg != null ? <span className="text-gray-400 text-[9px]">({fmtL(r.chg)})</span> : "")}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Cash / DII / Participants */}
      <div className="border-t border-gray-100 pt-2 space-y-1">
        {cashRows.map(r => (
          <div key={r.label} className="flex justify-between items-center">
            <span className="text-gray-500">{r.label}</span>
            <span className={`font-mono ${r.color}`}>{r.val ?? "—"}</span>
          </div>
        ))}
      </div>

      {/* Gift Nifty / DXY */}
      {(g?.gift_nifty_chg != null || g?.dxy_direction) && (
        <div className="border-t border-gray-100 mt-2 pt-2 flex gap-4">
          {g?.gift_nifty_chg != null && (
            <div>
              <p className="text-gray-400">Gift Nifty</p>
              <p className={`font-mono font-semibold ${chgColor(g.gift_nifty_chg)}`}>
                {g.gift_nifty_chg >= 0 ? "+" : ""}{g.gift_nifty_chg.toFixed(2)}%
              </p>
            </div>
          )}
          {g?.dxy_direction && (
            <div>
              <p className="text-gray-400">DXY</p>
              <p className={`font-mono font-semibold ${g.dxy_direction === "BEARISH" || g.dxy_direction === "FALLING" ? "text-green-600" : g.dxy_direction === "BULLISH" || g.dxy_direction === "RISING" ? "text-red-600" : "text-gray-500"}`}>
                {g.dxy_value != null ? g.dxy_value.toFixed(2) : g.dxy_direction}
              </p>
              {g.dxy_value != null && <p className="text-gray-400 text-xs">{g.dxy_direction}</p>}
            </div>
          )}
        </div>
      )}
      {!inst && <p className="text-gray-400 text-xs mt-2">Available after 08:45 AM</p>}
    </div>
  );
}

// ── Day stats ─────────────────────────────────────────────────────────────────
function DayStats({ status, signals = [] }) {
  const { outcomes = {}, signals_today = 0, market_open = false } = status || {};
  const winners = outcomes.WINNER || 0;
  const bigWinners = outcomes.BIG_WINNER || 0;
  // Manual exits are already booked as WINNER or LOSER on realised P/L, so they
  // need no bucket of their own here.
  const resolved = winners + bigWinners + (outcomes.LOSER || 0) + (outcomes.EXPIRED || 0);
  const winRate = resolved > 0 ? Math.round(((winners + bigWinners) / resolved) * 100) : null;
  const returnsWithData = signals.filter(s => s.return_pct != null && s.outcome != null);
  const avgReturn = returnsWithData.length > 0
    ? returnsWithData.reduce((sum, s) => sum + s.return_pct, 0) / returnsWithData.length
    : null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-gray-500 text-xs uppercase tracking-wider">Today's Stats</p>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${market_open ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
          {market_open ? "● Live" : "Closed"}
        </span>
      </div>
      <div className="grid grid-cols-6 gap-1 text-center">
        {[
          { label: "Signals",  n: signals_today,         color: "text-gray-800" },
          { label: "Winners",  n: winners,                color: "text-green-600" },
          { label: "Big Wins", n: bigWinners,             color: "text-emerald-600" },
          { label: "Losers",   n: outcomes.LOSER || 0,   color: "text-red-600" },
          { label: "Pending",  n: outcomes.pending || 0, color: "text-yellow-600" },
          { label: "Expired",  n: outcomes.EXPIRED || 0, color: "text-gray-400" },
        ].map(({ label, n, color }) => (
          <div key={label} className="bg-gray-50 rounded-lg py-2 px-1">
            <p className={`text-lg font-bold font-mono leading-none ${color}`}>{n}</p>
            <p className="text-gray-400 text-xs mt-1">{label}</p>
          </div>
        ))}
      </div>
      {(winRate != null || avgReturn != null) && (
        <div className="flex items-center justify-between mt-2">
          {winRate != null ? (
            <p className="text-xs text-gray-400">Win rate: <span className="text-gray-700 font-mono">{winRate}%</span></p>
          ) : <span />}
          {avgReturn != null && (
            <p className="text-xs text-gray-400">Avg return: <span className={`font-mono font-semibold ${avgReturn >= 0 ? "text-green-600" : "text-red-600"}`}>{avgReturn >= 0 ? "+" : ""}{avgReturn.toFixed(1)}%</span></p>
          )}
        </div>
      )}
    </div>
  );
}

const ALL_SYMBOLS = [
  "NIFTY", "BANKNIFTY",
  "HDFCBANK", "ICICIBANK", "SBIN", "AXISBANK", "KOTAKBANK",
  "BAJFINANCE", "BAJAJFINSV", "INDUSINDBK", "BANKBARODA", "RECLTD",
  "INFY", "TCS", "HCLTECH", "WIPRO", "TECHM",
  "RELIANCE", "LT", "ITC", "BHARTIARTL", "HINDUNILVR",
  "MARUTI", "TITAN", "ASIANPAINT", "ULTRACEMCO", "GRASIM",
  "M&M", "TATASTEEL", "TATAPOWER", "JSWSTEEL", "HINDALCO", "VEDL",
  "ONGC", "NTPC", "POWERGRID", "COALINDIA", "BPCL", "BEL", "ADANIPORTS",
  "SUNPHARMA", "DRREDDY", "CIPLA", "DIVISLAB",
  "TATACONSUM", "DMART", "TMPV", "APOLLOHOSP", "ETERNAL",
];

// ── Options chain panel ───────────────────────────────────────────────────────
function OptionsPanel({ marketOpen }) {
  const [symbol,   setSymbol]   = useState("NIFTY");
  const [expiry,   setExpiry]   = useState("");
  const [expiries, setExpiries] = useState([]);
  const [chain,    setChain]    = useState(null);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    setExpiries([]);
    setExpiry("");
    fetch(`${API}/api/options-expiries?symbol=${symbol}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.expiries?.length) {
          setExpiries(d.expiries);
          setExpiry(d.expiries[0]);
        }
      })
      .catch(() => {});
  }, [symbol]);

  const fetchChain = useCallback(async () => {
    if (!expiry) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/options-chain?symbol=${symbol}&expiry=${expiry}`);
      if (r.ok) setChain(await r.json());
    } catch (_) {}
    finally { setLoading(false); }
  }, [symbol, expiry]);

  useEffect(() => {
    fetchChain();
    const interval = setInterval(fetchChain, marketOpen ? POLL_FAST : 60_000);
    return () => clearInterval(interval);
  }, [fetchChain, marketOpen]);

  const rows    = chain?.strikes || [];
  const source  = chain?.source;
  const pcr     = chain?.pcr;
  const maxPain = chain?.max_pain;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-gray-500 text-xs uppercase tracking-wider">Options Chain</p>
          {source && (
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
              source === "live" ? "bg-green-100 text-green-700" :
              source === "eod"  ? "bg-purple-100 text-purple-700" :
              "bg-gray-100 text-gray-500"}`}>
              {source === "live" ? "● LIVE" : source === "eod" ? "EOD" : "REST"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={symbol}
            onChange={e => { setSymbol(e.target.value); setChain(null); }}
            className="text-xs bg-white text-gray-700 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-blue-400"
          >
            {ALL_SYMBOLS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {expiries.length > 0 && (
            <select
              value={expiry}
              onChange={e => { setExpiry(e.target.value); setChain(null); }}
              className="text-xs bg-white text-gray-700 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-blue-400 font-mono"
            >
              {expiries.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          )}
        </div>
      </div>

      {(pcr != null || maxPain != null) && (
        <div className="flex gap-4 mb-3 text-xs">
          {pcr != null && (
            <span className="text-gray-600">PCR: <span className={`font-mono font-bold ${pcr > 1.2 ? "text-green-600" : pcr < 0.8 ? "text-red-600" : "text-yellow-600"}`}>{pcr.toFixed(2)}</span></span>
          )}
          {maxPain != null && (
            <span className="text-gray-500">Max Pain: <span className="font-mono text-purple-600">{fmt(maxPain, 0)}</span></span>
          )}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <p className="text-gray-400 text-xs py-4 text-center">Loading chain...</p>
      ) : rows.length === 0 ? (
        <p className="text-gray-400 text-xs py-4 text-center">
          {!expiry ? "Loading expiries..." : marketOpen ? "Waiting for data..." : "No data"}
        </p>
      ) : (
        <OptionsChain chain={rows} spot={null} />
      )}
    </div>
  );
}

// ── Signals pagination ────────────────────────────────────────────────────────
function SignalsPaginated({ signals, loading, onExit, marketOpen }) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(signals.length / SIGNALS_PER_PAGE);
  const paged = signals.slice((page - 1) * SIGNALS_PER_PAGE, page * SIGNALS_PER_PAGE);
  const [selected, setSelected] = useState(null);

  // Reset to page 1 when signals change
  React.useEffect(() => { setPage(1); }, [signals.length]);

  return (
    <div>
      <LiveAlerts
        signals={paged}
        loading={loading}
        selectedId={selected?.id}
        onSelect={sig => setSelected(selected?.id === sig.id ? null : sig)}
        onExit={onExit}
        marketOpen={marketOpen}
      />
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-7 h-7 text-xs rounded border font-medium ${p === page ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [signals,        setSignals]        = useState([]);
  const [status,         setStatus]         = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [lastFetch, setLastFetch] = useState(null);

  const marketOpen = status?.market_open || false;
  const todayStr   = new Date().toDateString();

  const fetchData = useCallback(async () => {
    try {
      const [sigRes, statRes] = await Promise.all([
        fetch(`${API}/api/signals?limit=200`),
        fetch(`${API}/api/status`),
      ]);
      if (sigRes.ok)  { const d = await sigRes.json(); setSignals(d.signals || []); }
      if (statRes.ok) { setStatus(await statRes.json()); }
      setLastFetch(new Date());
    } catch (_) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, marketOpen ? POLL_FAST : POLL_SLOW);
    return () => clearInterval(interval);
  }, [fetchData, marketOpen]);

  const ip   = status?.index_prices  || {};
  const pcr  = status?.pcr           || {};
  const eod  = status?.eod           || {};
  const inst = status?.institutional || null;
  const glob = status?.global        || null;

  const todaySignals = signals.filter(
    s => s.created_at && new Date(s.created_at).toDateString() === todayStr
  );

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Market Dashboard</h1>
        <div className="flex items-center gap-3">
          {marketOpen && <span className="flex items-center gap-1 text-xs text-green-600"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />Live</span>}
          {lastFetch && (
            <span className="text-xs text-gray-400">
              {lastFetch.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
        </div>
      </div>

      {/* Row 1 — Index cards + VIX */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <IndexCard name="NIFTY"     data={ip.NIFTY}     pcr={pcr.NIFTY}     eod={eod.NIFTY} />
        <IndexCard name="BANKNIFTY" data={ip.BANKNIFTY} pcr={pcr.BANKNIFTY} eod={eod.BANKNIFTY} />
        <VixCard vix={status?.vix} />
      </div>

      {/* Row 2 — Day stats, then today's signals, then inst flow */}
      <DayStats status={status} signals={todaySignals} />

      {/* Today's signals */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Today's Signals</h2>
          <span className="text-xs text-gray-400">{todaySignals.length} signal{todaySignals.length !== 1 ? "s" : ""} today</span>
        </div>
        <SignalsPaginated
          signals={todaySignals}
          loading={loading}
          onExit={fetchData}
          marketOpen={marketOpen}
        />
      </div>

      <InstCard inst={inst} global={glob} />

      {/* Today's subscribed stock watchlist */}
      <WatchlistPanel watchlist={status?.watchlist || []} />

      {/* Options chain */}
      <OptionsPanel marketOpen={marketOpen} />
    </div>
  );
}

function WatchlistPanel({ watchlist }) {
  const [open, setOpen] = useState(false);
  if (!watchlist || watchlist.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
      <button
        className="flex items-center justify-between w-full text-left"
        onClick={() => setOpen(o => !o)}
      >
        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
          Today's Watchlist
        </span>
        <span className="text-xs text-gray-400">
          {watchlist.length} stocks {open ? "▲" : "▼"}
        </span>
      </button>
      {open && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {watchlist.map(sym => (
            <span key={sym} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700 font-mono">
              {sym}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
